import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Popup } from '@progress/kendo-react-popup';
import debounce from 'lodash/debounce';
import api from '@utils/axios';
import { listingLookupPath } from 'routes';
import I18n from '@utils/i18n';
import { SearchPanel, ResultRow, useNameOnlyPreference } from '@features/MaterialsServices/SearchPanel';
import Pagination from './Pagination';

function SearchDropdown({ proposedCorrection, setProposedCorrection }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchor = useRef();
  const popupRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [options, setOptions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(-1);
  const [nameOnly, setNameOnly] = useNameOnlyPreference();

  useEffect(() => {
    const closeDropdownOnScroll = () => {
      setDropdownOpen(false);
      setPage(-1);
    };

    const closeDropdownOnBlur = (e) => {
      if (anchor.current?.contains(e.target)) return;
      if (!popupRef.current?.contains(e.target)) {
        setDropdownOpen(false);
        setPage(-1);
      }
    };

    document.querySelector('.partition__workspace').addEventListener('scroll', closeDropdownOnScroll);
    document.body.addEventListener('click', closeDropdownOnBlur);

    return () => {
      document.querySelector('.partition__workspace').removeEventListener('scroll', closeDropdownOnScroll);
      document.body.removeEventListener('click', closeDropdownOnBlur);
    };
  }, []);

  const handleInputClick = () => {
    setPage(0);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setDropdownOpen(false);
    debounceSetPage(0);
  };

  const handleNameOnlyChange = (value) => {
    setNameOnly(value);
    setOptions([]);
    setPage(0);
  };

  const handleSelection = (selectedOption) => {
    setDropdownOpen(false);
    setProposedCorrection(selectedOption);
    setPage(-1);
    setInputValue('');
  };

  const debounceSetPage = useCallback(
    debounce(() => {
      setPage(-1);
      setPage(0);
    }, 800),
    []
  );

  useEffect(() => {
    if (page < 0) {
      setIsLoading(false);
      setTotalCount(0);
      setOptions([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    api
      .get(
        listingLookupPath({
          type: 'proposed_correction',
          search: inputValue,
          take: 20,
          skip: page * 20,
          name_only: nameOnly
        })
      )
      .then((response) => {
        if (cancelled) return;
        setTotalCount(response.data.total_count);
        setOptions((prevData) => (page === 0 ? response.data.data : [...prevData, ...response.data.data]));
      })
      .catch((error) => {
        if (cancelled) return;
        console.log(error);
        setTotalCount(0);
        setOptions([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
        setDropdownOpen(true);
      });

    return () => {
      cancelled = true;
    };
  }, [page, nameOnly]);

  return (
    <>
      {!proposedCorrection?.id && (
        <div className="qmb-input--text --x-full" ref={anchor} data-invalid="true">
          {isLoading && (
            <div
              id="spinner"
              className="qmb-loading--16"
              style={{
                position: 'absolute',
                right: '5px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10
              }}>
              <svg role="img">
                <use href="/map.svg#load-spinner" />
              </svg>
            </div>
          )}
          <input
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            placeholder={`${I18n.t('generic.search')}...`}
          />
        </div>
      )}
      <Popup anchor={anchor.current} show={dropdownOpen}>
        <SearchPanel
          nameOnly={nameOnly}
          onNameOnlyChange={handleNameOnlyChange}
          totalCount={totalCount}
          loading={isLoading}
          popupRef={popupRef}
          scrollRef={scrollContainerRef}>
          {options.map((option) => (
            <ResultRow key={option.id} option={option} term={inputValue} onSelect={handleSelection} />
          ))}
          {options.length === 0 && !isLoading && (
            <span className="material-search-panel__empty">{I18n.t('material_search.no_results')}</span>
          )}
          <Pagination
            totalCount={totalCount}
            options={options}
            setPage={setPage}
            isLoading={isLoading}
            scrollRootRef={scrollContainerRef}
          />
        </SearchPanel>
      </Popup>
    </>
  );
}

SearchDropdown.propTypes = {
  proposedCorrection: PropTypes.object,
  setProposedCorrection: PropTypes.func.isRequired
};

SearchDropdown.defaultProps = {
  proposedCorrection: {}
};

export default SearchDropdown;
